import React, { useEffect, useMemo } from "react";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Box } from "@mui/material";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom-v5-compat";

import useGetTransitionProposal from "@fartherfinance/frontend/api/TransitionProposals/hooks/useGetTransitionProposal";
import useGetTransitionProposalPerformanceVisibility from "@fartherfinance/frontend/api/TransitionProposals/hooks/useGetTransitionProposalPerformanceVisibility";
import { TransitionProposalId } from "@fartherfinance/frontend/api/TransitionProposals/types";
import { ClientId } from "@fartherfinance/frontend/api/Types";

import BaseLayout from "@src/multiCustodian/components/Layouts/BaseLayout/BaseLayout";
import TransitionProposalDetails from "@src/multiCustodian/components/TransitionProposals/TransitionProposalDetails/TransitionProposalDetails";
import {
  Section,
  SectionBody,
  SectionHeader,
} from "@src/multiCustodian/components/TransitionProposals/TransitionProposalDetails/TransitionProposalDetailsSections";
import useRequestAuth from "@src/multiCustodian/hooks/useRequestAuth";
import { trackEvent } from "@src/multiCustodian/services/tracking";
import IconButton from "@src/sharedComponents/IconButton/IconButton";
import PageHero from "@src/sharedComponents/PageHero/PageHero";
import TitleBlock from "@src/sharedComponents/PageHero/TitleBlock";
import Skeleton from "@src/sharedComponents/Skeleton/Skeleton";
import VerticalDivider from "@src/sharedComponents/VerticalDivider/VerticalDivider";

import styles from "./TransitionProposalComparison.module.css";

const TransitionProposalComparison: React.FC = () => {
  const navigate = useNavigate();
  const navigateBack = () => navigate(-1);
  const auth = useRequestAuth();
  const { clientId, transitionProposalId } = useParams<{
    clientId: ClientId;
    transitionProposalId: TransitionProposalId;
  }>();

  const {
    data: transitionProposalData,
    isLoading: isLoadingTransitionProposal,
  } = useGetTransitionProposal(transitionProposalId, auth);

  const { data: metricVisibilitySettings } =
    useGetTransitionProposalPerformanceVisibility(transitionProposalId, auth);

  // Track when client views the comparison screen
  useEffect(() => {
    if (transitionProposalData?.data) {
      trackEvent({
        name: "Client Compare Transition-Proposals",
        properties: {
          proposalId: transitionProposalId,
          proposalName: transitionProposalData.data.name,
          proposedPortfolioPlanId:
            transitionProposalData.data.proposedPortfolioPlanId,
          proposedPortfolioPlanVersion:
            transitionProposalData.data.proposedPortfolioPlanVersion,
          clientId,
        },
      });
    }
  }, [transitionProposalData?.data, transitionProposalId, clientId]);

  const metricVisibilitySettingsForCurrentPortfolio = useMemo(
    () => metricVisibilitySettings?.data.current || {},
    [metricVisibilitySettings?.data]
  );

  const metricVisibilitySettingsForProposedPortfolio = useMemo(
    () => metricVisibilitySettings?.data.proposed || {},
    [metricVisibilitySettings?.data]
  );

  const proposedPortfolioPlanId = useMemo(
    () => transitionProposalData?.data?.proposedPortfolioPlanId,
    [transitionProposalData]
  );

  const transitionProposalName = useMemo(
    () => transitionProposalData?.data?.name || "",
    [transitionProposalData?.data?.name]
  );

  return (
    <BaseLayout>
      <PageHero
        start={
          isLoadingTransitionProposal ? (
            <Skeleton width={400} height={40} />
          ) : (
            <TitleBlock title={transitionProposalName} />
          )
        }
      />

      <IconButton
        size="small"
        className={styles.backButton}
        onClick={navigateBack}
        IconComponent={ArrowBackIcon}
      />

      <Box className={styles.container}>
        <Section className={styles.section}>
          <SectionHeader title="Current Portfolio" />
          <SectionBody>
            <TransitionProposalDetails
              transitionProposalId={transitionProposalId}
              metricVisibilitySettings={
                metricVisibilitySettingsForCurrentPortfolio
              }
            />
          </SectionBody>
        </Section>

        <VerticalDivider label={"vs."} />

        <Section className={styles.section}>
          <SectionHeader
            title={
              isLoadingTransitionProposal ? (
                <Skeleton width={200} height={20} />
              ) : (
                transitionProposalName
              )
            }
          />
          <SectionBody>
            {proposedPortfolioPlanId ? (
              <TransitionProposalDetails
                portfolioPlanId={proposedPortfolioPlanId}
                metricVisibilitySettings={
                  metricVisibilitySettingsForProposedPortfolio
                }
              />
            ) : null}
          </SectionBody>
        </Section>
      </Box>
    </BaseLayout>
  );
};

export default TransitionProposalComparison;
